import { ConfirmModalState, confirmModalState } from '@/stores/cvc';
import { useCallback } from 'react';
import { useRecoilState, useSetRecoilState } from 'recoil';

/**
 * WRITE-ONLY access to the confirm modal, for callers that raise one but never
 * render it.
 *
 * Split out of `useConfirmModal` because that hook reads the atom with
 * `useRecoilState`, which SUBSCRIBES its component. Every upload and
 * generation call site now reaches this file through `useSessionNotice`, so
 * the read form would have newly subscribed AudioEditorPanel, GeneratePanel,
 * SeparateControl and useResultUpload to every confirm-modal open and close
 * anywhere in the app — dismissing the "Use as Source" duplicate warning would
 * re-render the whole audio editor. `useSetRecoilState` takes the setter
 * without the subscription.
 *
 * Memoised because the identity propagates: each notifier is wrapped in a
 * useCallback that lists it as a dependency, and a function rebuilt every
 * render would make all of those no-ops and hand a fresh prop to the
 * virtualised file lists. The Recoil setter is stable, so the closure never
 * needs rebuilding.
 */
export const useOpenConfirmModal = () => {
  const setModalState = useSetRecoilState(confirmModalState);

  return useCallback(
    (config: Omit<ConfirmModalState, 'isOpen'>) => {
      setModalState({
        isOpen: true,
        ...config,
      });
    },
    [setModalState]
  );
};

const useConfirmModal = () => {
  const [modalState, setModalState] = useRecoilState(confirmModalState);

  // One implementation of "open", shared with the write-only hook above.
  const openModal = useOpenConfirmModal();

  const closeModal = useCallback(() => {
    setModalState({
      isOpen: false,
    });
  }, [setModalState]);

  return {
    openConfirmModal: openModal,
    closeConfirmModal: closeModal,
    confirmModalState: modalState,
  };
};

export default useConfirmModal;
